import { Flex, Icon, Text } from '@chakra-ui/react';
import { LucideIcon } from 'lucide-react';
import { FC } from 'react';

interface ChecklistItemRowProps {
  /** The checklist item title (activity name, or document name when ungrouped). */
  title: string;
  isSelected: boolean;
  onSelect: () => void;
  /** Optional leading icon (e.g. file-check for checklist items, file for standalone). */
  icon?: LucideIcon;
  /** Optional muted secondary line under the title (e.g. "Not on checklist"). */
  caption?: string;
}

/**
 * A single selectable checklist-item row in the left rail. Selecting it opens
 * the item's documents as tabs in the center. The active row gets a solid
 * highlight; the rest are muted with a hover affordance. An optional leading
 * icon + caption distinguish standalone (not-on-checklist) documents.
 */
export const ChecklistItemRow: FC<ChecklistItemRowProps> = ({
  title,
  isSelected,
  onSelect,
  icon,
  caption,
}) => (
  <Flex
    _hover={{
      bg: isSelected ? 'compliance.group.expanded.bg' : 'compliance.hover.bg',
    }}
    align={caption != null ? 'flex-start' : 'center'}
    aria-current={isSelected ? 'true' : undefined}
    bg={isSelected ? 'compliance.group.expanded.bg' : 'transparent'}
    borderRadius="8px"
    cursor="pointer"
    gap={2}
    overflow="hidden"
    px={2.5}
    py={2}
    role="button"
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect();
      }
    }}
  >
    {icon != null && (
      <Icon
        as={icon}
        boxSize="16px"
        color={isSelected ? 'compliance.text.primary' : 'compliance.text.muted'}
        flexShrink={0}
        mt={caption != null ? '2px' : undefined}
      />
    )}
    <Flex direction="column" gap={0.5} minW={0}>
      <Text
        color={
          isSelected ? 'compliance.text.primary' : 'compliance.text.secondary'
        }
        fontSize="sm"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {title}
      </Text>
      {caption != null && (
        <Text color="compliance.text.muted" fontSize="xs">
          {caption}
        </Text>
      )}
    </Flex>
  </Flex>
);
